import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { XCloseIcon, ChevronDownIcon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { TAG_LABELS, getColorFromTag } from '@/lib/tags'
import { usePermissions } from '@/hooks/api'

interface TagSelectorProps {
  selectedTags: string[]
  onTagsChange: (tags: string[]) => void
  className?: string
}

export function TagSelector({
  selectedTags,
  onTagsChange,
  className,
}: TagSelectorProps) {
  const { t } = useTranslation()
  const { canManage } = usePermissions()

  const BASIC_TAGS: Record<string, string> = {
    ':thumbs-up:': t('components.tag_selector.thumbs_up'),
    ':thumbs-down:': t('components.tag_selector.thumbs_down'),
  }

  const ADMIN_TAGS: Record<string, string> = {
    ':needs-review:': t('components.tag_selector.needs_review'),
    ':reviewed:': t('components.tag_selector.reviewed'),
    ':finetuning:': t('components.tag_selector.finetuning'),
    ':example:': t('components.tag_selector.example'),
  }
  const [isOpen, setIsOpen] = useState(false)
  const [localTags, setLocalTags] = useState<string[]>(selectedTags || [])

  const isAdmin = canManage
  const availableTags = isAdmin
    ? { ...BASIC_TAGS, ...ADMIN_TAGS }
    : BASIC_TAGS

  // Update local tags when props change
  useEffect(() => {
    setLocalTags(selectedTags || [])
  }, [selectedTags])

  const handleToggleTag = useCallback(
    (tag: string) => {
      const newTags = localTags.includes(tag)
        ? localTags.filter((t) => t !== tag)
        : [...localTags, tag]

      setLocalTags(newTags)
      onTagsChange(newTags)
    },
    [localTags, onTagsChange]
  )

  const handleRemoveTag = useCallback(
    (tag: string, e: React.MouseEvent) => {
      e.stopPropagation()
      const newTags = localTags.filter((t) => t !== tag)
      setLocalTags(newTags)
      onTagsChange(newTags)
    },
    [localTags, onTagsChange]
  )

  const getTagColor = (tag: string) => {
    const baseColor = getColorFromTag(tag)
    // Create darker text color (0.7 multiplier)
    const rgb = baseColor.match(/[A-Za-z0-9]{2}/g)
    if (rgb) {
      const [r, g, b] = rgb.map((c) => Math.floor(parseInt(c, 16) * 0.7))
      const textColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
      return {
        backgroundColor: `${baseColor}40`, // 25% opacity
        borderColor: `${baseColor}45`,
        color: textColor,
      }
    }
    return {}
  }

  return (
    <div className={cn('relative', className)}>
      {/* Selected Tags Display / Dropdown Trigger */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex min-h-[42px] w-full cursor-pointer flex-wrap items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 transition-colors hover:border-brand-500 dark:border-gray-600 dark:bg-gray-800',
          isOpen && 'border-brand-500 dark:border-brand-400'
        )}
      >
        {localTags.length === 0 ? (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {t('components.tag_selector.select_tags')}
          </span>
        ) : (
          localTags.map((tag) => (
            <span
              key={tag}
              style={getTagColor(tag)}
              className="inline-flex items-center gap-1 rounded-lg border-[1.5px] px-2 py-0.5 text-xs font-bold"
            >
              {availableTags[tag as keyof typeof availableTags] || tag}
              <button
                onClick={(e) => handleRemoveTag(tag, e)}
                className="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
              >
                <XCloseIcon className="h-3 w-3" />
              </button>
            </span>
          ))
        )}
        <ChevronDownIcon
          className={cn(
            'ml-auto h-4 w-4 text-gray-500 transition-transform dark:text-gray-400',
            isOpen && 'rotate-180'
          )}
        />
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
            {Object.entries(availableTags).map(([tagId, tagLabel]) => {
              const isSelected = localTags.includes(tagId)
              return (
                <button
                  key={tagId}
                  onClick={() => handleToggleTag(tagId)}
                  className={cn(
                    'flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors hover:bg-gray-50 dark:hover:bg-gray-700',
                    isSelected && 'bg-brand-50 dark:bg-brand-950'
                  )}
                >
                  {/* Checkbox */}
                  <div
                    className={cn(
                      'flex h-4 w-4 items-center justify-center rounded border-2',
                      isSelected
                        ? 'border-brand-500 bg-brand-500'
                        : 'border-gray-300 dark:border-gray-600'
                    )}
                  >
                    {isSelected && (
                      <svg
                        className="h-3 w-3 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={3}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>

                  {/* Tag Label */}
                  <span
                    className={cn(
                      'text-gray-900 dark:text-gray-100',
                      isSelected && 'font-medium'
                    )}
                  >
                    {tagLabel}
                  </span>
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

export default TagSelector
